<template>
    <ul 
        :class="{ 
            'plan__list': type === 'inner',
            'landing__pricing__cards__list': type === 'landing',
            'landing__pricing__cards__list pricing__cards__list': type === 'pricing'
        }"
        v-bind="$attrs"
    >
        <li 
            v-for="item in pricingInfoData" 
            :key="item.plan" 
            :class="{ 
                'active': isPlanActive(item.plan) && type !== 'inner',
                'plan__list__item': type === 'inner',
                'landing__pricing__cards__list__item': type === 'landing',
                'landing__pricing__cards__list__item pricing__cards__item': type === 'pricing',
            }" 
        >
            <div class="inner">
                <div>    
                    <div class="mt--5 flex align-items-center justify-content-between">
                        <p class="text-gray-800 text--upper plan__badge">
                            {{ item.plan }}
                        </p>
                        <template v-if="isPlanActive(item.plan)">
                            <span class="tag--pink text--xs tag bold badge rounded-pill">
                                Current plan
                            </span>
                        </template>
                    </div>

                    <div class="mt--10 mb--10">
                        <div class="flex align-items-baseline">
                            <span class="text-gray-800 text--3xl text--semi--bold mb--0 mr--5">
                                {{ currencySymbol }}{{ getItemAmt(item) }}
                            </span>
                            <span class="text--xs text-gray-500 mb--0 text--semi--bold">/month</span>
                        </div>
                        <p class="text-gray-700 text--sm mt--3">
                            {{ item.description }}
                        </p>
                    </div>

                    <Divider style="color: #000;" />
    
                    <div class="mt--20">
                        <p class="text--sm text-gray-800 text--semi--bold mb--15">
                            What you get:
                        </p>

                        <div v-for="plan in item.allowedFeatures" :key="plan" class="flex align-items-center mb--15">
                            <div class="plans__check">
                                <IconSvg name="check-successful-outline" />
                            </div>
                            <span class="ml--10 text--sm text-gray-800">
                                {{ plan }}
                            </span>
                        </div>
                    </div>
                </div>

                <div>
                    <BaseButton 
                        size="md" 
                        full-width
                        label="Get started" 
                        class="mt--20 text--capitalize" 
                        :disabled="isPlanActive(item.plan) || (loading && selectedPlan === item.plan) || btnDisabled"
                        @submit="$emit('set-plan', item.plan)"
                    />
                </div>
            </div>
        </li>
    </ul>
</template>

<script>
import { BaseButton, IconSvg } from "@/components/ui/index";
import { pricingInfoData } from "@/utils/dummy";
import Divider from "@/components/ui/divider/Divider.vue";
import { supportedCurrenciesMap } from "@/plugins/countries";

export default {
    name: "PlansComponent",

    components: {
        BaseButton, 
        IconSvg, 
        Divider,
    },

    props: {
        type: {
            type: String,
            default: "landing",
        },

        activePlan: {
            type: String,
            default: "",
        },

        btnDisabled: {
            type: Boolean,
            default: false,
        },

        selectedPlan: {
            type: String,
            default: "",
        },

        currency: {
            type: String,
            default: "USD",
        },

        loading: {
            type: Boolean,
            default: false,
        },
    },

    emits: ["set-plan"],

    data() {
        return {
            pricingInfoData,
            accountPlans: {
                free: {
                    amount: {
                        USD: {
                            monthlyAmount: 0,
                            yearlyAmount: 0,
                        },
                        NGN: {
                            monthlyAmount: 0,
                            yearlyAmount: 0,
                        },
                        EUR: {
                            monthlyAmount: 0,
                            yearlyAmount: 0,
                        },
                    },
                },
                standard: {
                    amount: {
                        USD: {
                            monthlyAmount: 4.99,
                            yearlyAmount: 58,
                        },
                        NGN: {
                            monthlyAmount: 4200,
                            yearlyAmount: 50400,
                        },
                        EUR: {
                            monthlyAmount: 3.44,
                            yearlyAmount: 40,
                        },
                    },
                },
                plus: {
                    amount: {
                        USD: {
                            monthlyAmount: 7.99,
                            yearlyAmount: 95,
                        },
                        NGN: {
                            monthlyAmount: 8999,
                            yearlyAmount: 106000,
                        },
                        EUR: {
                            monthlyAmount: 6.25,
                            yearlyAmount: 74.55,
                        },
                    },
                },
            },
        };
    },

    computed: {
        currencySymbol() {
            return supportedCurrenciesMap[this?.currency?.toLowerCase()]?.symbol || "$";
        },

        systemPlans() {
            return this.$store.getters["config/systemPlans"];
        },
    },

    methods: {
        isPlanActive(plan) {            
            return plan?.toLowerCase() === this.activePlan?.toLowerCase();
        },

        getItemAmt(item) {            
            if (Object.keys(this.systemPlans).length) return this.$format_amount(this.systemPlans[item?.plan?.toLowerCase()]?.amount[this?.currency?.toUpperCase()]?.monthlyAmount, 2) || 0;
            return this.$format_amount(this.accountPlans[item?.plan?.toLowerCase()]?.amount[this?.currency?.toUpperCase()]?.monthlyAmount, 2) || 0;
        },
    },
};
</script>

<style lang="scss" scoped>
   .plan {
        &__heading {
            margin-bottom: 3rem;
        }

        &__title {
            font-size: 1.5rem;
            font-weight: 500;
            margin-bottom: 5px;
        }

        &__list {
            display: flex;
            flex-wrap: wrap;
            width: 100%;
            height: auto;

            &__item {
                width: 31%;
                position: relative;
                transition: all 0.2s ease-out 0s, visibility 0s;
                --transition: all 0.2s ease-out 0s, visibility 0s;
                padding: 15px;
                border-radius: 10px;
                border: 1px solid #ededed;
                justify-content: space-evenly;

                &:not(:last-of-type) {
                    margin-right: 1rem;
                }

                &.active {
                    border: 1px solid #228E9D;
                }

                .inner {
                    display: flex;
                    flex-direction: column;
                    justify-content: space-between;
                    height: 100%;
                }
            }
        }

        &__badge {
            // box-shadow: 0 0 #0000, 0 0 #0000, 0 0 #0000, 0 0 #0000, 0 1px 3px 0 #0000001a,0 1px 2px -1px #0000001a;
            font-size: 0.875rem;
            padding-block: calc(0.25rem * 1);
            display: inline-flex;
            border-radius: 1.5rem;
            z-index: 20;
            padding-inline: calc(0.5rem * 2);
            font-weight: 500;
            border: 1px solid #1f2937;
        }
    }
</style>